/**
 * Phase 1 RBAC stubs for ATW Agents.
 *
 * Product model:
 * - Super Admin: full control inside the Agent Organization
 * - Standard: built-in, required, view-only by default
 * - Custom roles: Super Admin creates later with permission rules
 *
 * This module is UI-facing only until real membership APIs exist.
 */

import type { AgentsHumanRoleId } from "@/lib/agentsDemoData";

export type AgentsPermission =
  | "view_all"
  | "manage_users"
  | "manage_roles"
  | "manage_connections"
  | "manage_schedules"
  | "manage_agent_configuration"
  | "create_edit_archive_agents"
  | "manage_credits_plan"
  | "act_on_escalations"
  | "manage_billing";

const SUPER_ADMIN_PERMS: AgentsPermission[] = [
  "view_all",
  "manage_users",
  "manage_roles",
  "manage_connections",
  "manage_schedules",
  "manage_agent_configuration",
  "create_edit_archive_agents",
  "manage_credits_plan",
  "act_on_escalations",
  "manage_billing",
];

const STANDARD_PERMS: AgentsPermission[] = ["view_all"];

export function permissionsForRole(role: AgentsHumanRoleId): AgentsPermission[] {
  if (role === "super_admin") return SUPER_ADMIN_PERMS;
  if (role === "standard") return STANDARD_PERMS;
  // Custom roles: treat as view-only until rules are editable.
  return STANDARD_PERMS;
}

export function mergePermissions(roles: AgentsHumanRoleId[]): Set<AgentsPermission> {
  const out = new Set<AgentsPermission>();
  for (const role of roles) {
    for (const p of permissionsForRole(role)) out.add(p);
  }
  return out;
}

export function can(roles: AgentsHumanRoleId[], permission: AgentsPermission): boolean {
  return mergePermissions(roles).has(permission);
}

export function isViewOnly(roles: AgentsHumanRoleId[]): boolean {
  const perms = mergePermissions(roles);
  if (perms.has("manage_agent_configuration")) return false;
  if (perms.has("create_edit_archive_agents")) return false;
  if (perms.has("manage_connections")) return false;
  if (perms.has("manage_schedules")) return false;
  if (perms.has("manage_users")) return false;
  if (perms.has("manage_billing")) return false;
  return perms.has("view_all");
}

export function primaryRoleLabel(roles: AgentsHumanRoleId[]): string {
  if (roles.includes("super_admin")) return "Super Admin";
  if (roles.includes("standard")) return "Standard";
  return roles[0] || "Standard";
}
